import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import './event-modals.css';

const EditEventModal = ({ isOpen, onClose, onSave, event }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'UPCOMING',
    tags: '',
    image_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Populate form when event changes
  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        status: event.status || 'UPCOMING',
        tags: Array.isArray(event.tags) ? event.tags.join(', ') : '',
        image_url: event.image_url || ''
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Parse tags
      const tags = formData.tags
        ? formData.tags.split(',').map(t => t.trim()).filter(t => t)
        : [];

      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
        tags: tags.length > 0 ? tags : null,
        image_url: formData.image_url.trim() || null
      };

      await onSave(updateData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Event" size="lg" className="event-modal">
      <form onSubmit={handleSubmit} className="event-modal-form">
        <div className="event-modal-intro">
          <strong>{event?.title || 'Event details'}</strong>
          <span>Update the event metadata used across event lists, details, and report generation.</span>
        </div>

        <label className="event-modal-field" htmlFor="title">
          <span>Title <strong>*</strong></span>
          <input
            id="title"
            name="title"
            type="text"
            value={formData.title}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="Enter event title"
            disabled={loading}
            autoFocus
          />
        </label>

        <label className="event-modal-field" htmlFor="description">
          <span>Description</span>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="event-modal-control"
            placeholder="Enter event description"
            disabled={loading}
          />
        </label>

        <label className="event-modal-field" htmlFor="status">
          <span>Status <strong>*</strong></span>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="event-modal-control"
            disabled={loading}
          >
            <option value="UPCOMING">Upcoming</option>
            <option value="ONGOING">Ongoing</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </label>

        <label className="event-modal-field" htmlFor="tags">
          <span>Tags</span>
          <input
            id="tags"
            name="tags"
            type="text"
            value={formData.tags}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="Enter tags separated by commas"
            disabled={loading}
          />
          <small>Separate multiple tags with commas, for example: conference, diplomacy, UN.</small>
        </label>

        <label className="event-modal-field" htmlFor="image_url">
          <span>Image URL</span>
          <input
            id="image_url"
            name="image_url"
            type="url"
            value={formData.image_url}
            onChange={handleChange}
            className="event-modal-control"
            placeholder="https://example.com/image.jpg"
            disabled={loading}
          />
        </label>

        {error && (
          <div className="event-modal-error">{error}</div>
        )}

        <div className="event-modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="event-modal-btn"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="event-modal-btn event-modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditEventModal;

